// shared/mock/mockData.ts
// Global mock data for authentication, users, and departments

export interface GlobalUser {
  id: number;
  username: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'employee' | 'supervisor';
  department: 'Reliability' | 'Automation' | null;
  services: string[];
  is_active: boolean;
}

export interface Department {
  id: number;
  name: 'Reliability' | 'Automation';
  description: string;
  manager_user_id: number;
}

// Department definitions
export const DEPARTMENTS = {
  RELIABILITY: { id: 1, name: 'Reliability' as const, description: 'System Reliability and Maintenance', manager_user_id: 2 },
  AUTOMATION: { id: 2, name: 'Automation' as const, description: 'Process Automation and DevOps', manager_user_id: 8 }
} as const;

export const mockDepartments: Department[] = [
  DEPARTMENTS.RELIABILITY,
  DEPARTMENTS.AUTOMATION
];

// Mock users data (IT creates and manages these)
export const mockUsers: GlobalUser[] = [
  // Admin user
  {
    id: 1,
    username: 'admin',
    name: 'System Administrator',
    email: 'admin@company.com',
    role: 'admin',
    department: null,
    services: ['management', 'people', 'nfe', 'support', 'devops', 'inventory', 'validation', 'dms', 'vod', 'access'],
    is_active: true
  },

  // Reliability Department
  {
    id: 2,
    username: 'sarah.manager',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@company.com',
    role: 'manager',
    department: 'Reliability',
    services: ['management', 'people', 'support', 'devops', 'inventory', 'validation', 'dms'],
    is_active: true
  },
  {
    id: 3,
    username: 'john.reliability',
    name: 'John Smith',
    email: 'john.smith@company.com',
    role: 'employee',
    department: 'Reliability',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 4,
    username: 'jane.reliability',
    name: 'Jane Doe',
    email: 'jane.doe@company.com',
    role: 'employee',
    department: 'Reliability',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 5,
    username: 'mike.reliability',
    name: 'Mike Wilson',
    email: 'mike.wilson@company.com',
    role: 'employee',
    department: 'Reliability',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 6,
    username: 'lisa.reliability',
    name: 'Lisa Brown',
    email: 'lisa.brown@company.com',
    role: 'employee',
    department: 'Reliability',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 7,
    username: 'tom.reliability',
    name: 'Tom Davis',
    email: 'tom.davis@company.com',
    role: 'employee',
    department: 'Reliability',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 15,
    username: 'supervisor.test',
    name: 'Alex Supervisor',
    email: 'alex.supervisor@company.com',
    role: 'supervisor',
    department: 'Reliability',
    services: ['management', 'people', 'support', 'inventory', 'validation'],
    is_active: true
  },

  // Automation Department
  {
    id: 8,
    username: 'carlos.manager',
    name: 'Carlos Rodriguez',
    email: 'carlos.rodriguez@company.com',
    role: 'manager',
    department: 'Automation',
    services: ['management', 'people', 'support', 'devops', 'inventory', 'validation', 'dms'],
    is_active: true
  },
  {
    id: 9,
    username: 'anna.automation',
    name: 'Anna Garcia',
    email: 'anna.garcia@company.com',
    role: 'employee',
    department: 'Automation',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 10,
    username: 'david.automation',
    name: 'David Lee',
    email: 'david.lee@company.com',
    role: 'employee',
    department: 'Automation',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 11,
    username: 'emma.automation',
    name: 'Emma Taylor',
    email: 'emma.taylor@company.com',
    role: 'employee',
    department: 'Automation',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 12,
    username: 'ryan.automation',
    name: 'Ryan Miller',
    email: 'ryan.miller@company.com',
    role: 'employee',
    department: 'Automation',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  },
  {
    id: 13,
    username: 'sofia.automation',
    name: 'Sofia Anderson',
    email: 'sofia.anderson@company.com',
    role: 'employee',
    department: 'Automation',
    services: ['management', 'nfe', 'support', 'inventory', 'validation'],
    is_active: true
  }
];

// Utility functions for user management
export const getUserById = (id: number): GlobalUser | undefined => {
  return mockUsers.find(user => user.id === id);
};

export const getUserByUsername = (username: string): GlobalUser | undefined => {
  return mockUsers.find(user => user.username.toLowerCase() === username.toLowerCase());
};

export const getUsersByDepartment = (department: 'Reliability' | 'Automation'): GlobalUser[] => {
  return mockUsers.filter(user => user.department === department);
};

export const getUsersByRole = (role: 'admin' | 'manager' | 'employee' | 'supervisor'): GlobalUser[] => {
  return mockUsers.filter(user => user.role === role);
};

export const hasServiceAccess = (user: GlobalUser, serviceId: string): boolean => {
  return user.services.includes(serviceId);
};

export const getManagersForDepartment = (department: 'Reliability' | 'Automation'): GlobalUser[] => {
  return mockUsers.filter(user => 
    user.role === 'manager' && 
    user.department === department
  );
};

export const getEmployeesForDepartment = (department: 'Reliability' | 'Automation'): GlobalUser[] => {
  return mockUsers.filter(user => 
    user.role === 'employee' && 
    user.department === department
  );
};

// Mock authentication functions
export const mockLogin = async (username: string, password: string): Promise<GlobalUser> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  const user = getUserByUsername(username);
  if (!user || !user.is_active) {
    throw new Error('Invalid username or password');
  }

  // For demo purposes, any password works
  return user;
};

export const mockLogout = (): void => {
  // Clear any session data if needed
};

// Service access validation
export const validateServiceAccess = (user: GlobalUser, serviceId: string): boolean => {
  if (user.role === 'admin') {
    return true; // Admin has access to everything
  }

  return hasServiceAccess(user, serviceId);
};

// Department access validation for managers
export const validateDepartmentAccess = (
  managerUser: GlobalUser, 
  targetDepartment: 'Reliability' | 'Automation'
): boolean => {
  if (managerUser.role === 'admin') {
    return true; // Admin can access all departments
  }

  if (managerUser.role === 'manager') {
    return managerUser.department === targetDepartment;
  }

  return false; // Employees can't access department management
};

// Get department info
export const getDepartmentById = (id: number): Department | undefined => {
  return mockDepartments.find(dept => dept.id === id);
};

export const getDepartmentByName = (name: 'Reliability' | 'Automation'): Department | undefined => {
  return mockDepartments.find(dept => dept.name === name);
};

// Export default data collections
export const mockData = {
  users: mockUsers,
  departments: mockDepartments,
  DEPARTMENTS
};
